import { PropsWithChildren, ReactNode } from "react";
import Button from "components/elements/Button/Button";
import FileDropzone from "components/elements/Upload/FileDropzone";
import FileAddSvg from "components/icons/24/actions/file-add.svg";
import AutodeskLogoSvg from "components/icons/autodesk-logo.svg";
import { useCurrentProjectId } from "lib/hooks/useProject";
import { useWorkspaceEntitlementsStatusByProjectId } from "lib/hooks/useWorkspaceEntitlementsStatusByProjectId";
import SpeckleLogoSvg from "./speckle-logo.svg";

export default function UploadSourceChooser(p: {
  hasSpeckle: boolean;
  onBrowse(): void;
  onAddFiles(files: File[]): void;
  onSpeckleImport(): void;
  onAutodeskImport(): void;
}) {
  const { workspaceEntitlementsStatus } =
    useWorkspaceEntitlementsStatusByProjectId(useCurrentProjectId());
  const hasConnectors = !!workspaceEntitlementsStatus?.hasConnectors;
  const isBusinessPlanRequired =
    !!workspaceEntitlementsStatus && !hasConnectors;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-lg">Add assets</h1>
        <p className="text-sm font-light text-gray-500">
          Select how you&apos;d like to add models to your project
        </p>
      </header>
      <div className="flex gap-4">
        <div className="flex-1">
          <FileDropzone onAddFiles={p.onAddFiles}>
            <SourceCard
              icon={<FileAddSvg className="w-8 text-gray-400" />}
              title="Upload files"
              description="Drag model files onto this card, or upload them from your computer"
            >
              <Button slim onClick={p.onBrowse}>
                Upload files
              </Button>
            </SourceCard>
          </FileDropzone>
        </div>
        <div className="flex-1">
          <SourceCard
            icon={<AutodeskLogoSvg className="w-32" />}
            title="Import from Autodesk Forma"
            description="Connect to Autodesk Forma and import models directly"
            note={isBusinessPlanRequired && "Available on the Business plan"}
          >
            <Button
              theme="primary-outline"
              slim
              disabled={!hasConnectors}
              onClick={p.onAutodeskImport}
            >
              Import models
            </Button>
          </SourceCard>
        </div>
        {p.hasSpeckle && (
          <div className="flex-1">
            <SourceCard
              icon={<SpeckleLogoSvg className="w-8" />}
              title="Import from Speckle"
              description="Connect to a Speckle server and import models directly"
            >
              <Button theme="primary-outline" slim onClick={p.onSpeckleImport}>
                Import models
              </Button>
            </SourceCard>
          </div>
        )}
      </div>
    </div>
  );
}

function SourceCard(
  p: PropsWithChildren<{
    icon: ReactNode;
    title: string;
    description: string;
    note?: ReactNode;
  }>,
) {
  return (
    <section className="flex h-full flex-col gap-3 rounded-lg border p-5">
      <div className="flex h-8 items-center">{p.icon}</div>
      <h2 className="font-medium">{p.title}</h2>
      <p className="flex-1 text-sm font-light text-gray-500">{p.description}</p>
      <div>{p.children}</div>
      {p.note && <p className="text-xs text-gray-500">{p.note}</p>}
    </section>
  );
}
